import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-[#64748B]">{label}</Label>
    {children}
  </div>
);

const inputCls = "h-9 rounded-xl border-0 neu-inset text-xs focus-visible:ring-[#3D3DF0]/40";

export const TextField = ({ label, value, onChange, testid, type = "text" }) => (
  <Field label={label}>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} className={inputCls} data-testid={testid} />
  </Field>
);

export const AreaField = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} className="rounded-xl border-0 neu-inset text-xs" data-testid={testid} />
  </Field>
);

export const SelectField = ({ label, value, onChange, options, testid }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="border-[#DCE3EE] bg-[#EEF2F8]">
        {options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testid}-opt-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);

export const SwitchField = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-[#334155]">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const MediaField = ({ label, value, onChange, accept, bookId, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/books/${bookId}/assets`, fd);
      onChange(data.path);
      toast.success(`${f.name} uploaded`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <Field label={label}>
      <div className="flex gap-1.5">
        <Input value={value ?? ""} placeholder="Paste URL or upload" onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`${testid}-url`} />
        <button onClick={() => ref.current.click()} disabled={busy} aria-label="Upload file" data-testid={`${testid}-upload`} className="grid h-8 w-9 shrink-0 place-items-center rounded-xl neu-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        </button>
        <input ref={ref} type="file" accept={accept} hidden onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file`} />
      </div>
    </Field>
  );
};
